import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);

    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        projectType: '',
        budget: '',
        message: ''
      });
    }, 3000);
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stagger card entrance animations
            [0, 1, 2, 3].forEach((index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, index]);
              }, index * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" className="contact-section" ref={sectionRef}>
      <div className="container">
        <div className="section-header fade-in">
          <div className="section-badge">Get in Touch</div>
          <h2 className="section-title">
            Let's Build Something
            <span className="gradient-text"> Amazing Together</span>
          </h2>
          <p className="section-description">
            Ready to transform your e-commerce vision into reality? Tell us about your
            project and let's start the conversation.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-form-wrapper fade-in">
            <div className="form-background">
              <div className="form-glow"></div>
              <div className="form-particles">
                <span className="form-particle"></span>
                <span className="form-particle"></span>
                <span className="form-particle"></span>
              </div>
            </div>

            {submitted ? (
              <div className="success-message">
                <div className="success-icon-wrapper">
                  <div className="success-icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="#10B981"/>
                      <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="success-rings">
                    <div className="success-ring"></div>
                    <div className="success-ring"></div>
                    <div className="success-ring"></div>
                  </div>
                </div>
                <h3>Thank You!</h3>
                <p>We've received your message and will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className={`form-group ${focusedField === 'name' ? 'focused' : ''} ${formData.name ? 'filled' : ''}`}>
                    <label htmlFor="name">Full Name *</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        required
                        placeholder="John Doe"
                      />
                      <div className="input-border"></div>
                    </div>
                  </div>
                  <div className={`form-group ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'filled' : ''}`}>
                    <label htmlFor="email">Email Address *</label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        required
                        placeholder="john@company.com"
                      />
                      <div className="input-border"></div>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className={`form-group ${focusedField === 'company' ? 'focused' : ''} ${formData.company ? 'filled' : ''}`}>
                    <label htmlFor="company">Company Name</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('company')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Your Company"
                      />
                      <div className="input-border"></div>
                    </div>
                  </div>
                  <div className={`form-group ${focusedField === 'projectType' ? 'focused' : ''} ${formData.projectType ? 'filled' : ''}`}>
                    <label htmlFor="projectType">Project Type *</label>
                    <div className="input-wrapper">
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('projectType')}
                        onBlur={() => setFocusedField(null)}
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="new-store">New Shopify Store</option>
                        <option value="redesign">Store Redesign</option>
                        <option value="optimization">Optimization</option>
                        <option value="support">Ongoing Support</option>
                        <option value="other">Other</option>
                      </select>
                      <div className="input-border"></div>
                    </div>
                  </div>
                </div>

                <div className={`form-group ${focusedField === 'budget' ? 'focused' : ''} ${formData.budget ? 'filled' : ''}`}>
                  <label htmlFor="budget">Budget Range</label>
                  <div className="input-wrapper">
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('budget')}
                      onBlur={() => setFocusedField(null)}
                    >
                      <option value="">Select your budget</option>
                      <option value="1000-2500">$1,000 - $2,500</option>
                      <option value="2500-5000">$2,500 - $5,000</option>
                      <option value="5000-10000">$5,000 - $10,000</option>
                      <option value="10000+">$10,000+</option>
                    </select>
                    <div className="input-border"></div>
                  </div>
                </div>

                <div className={`form-group ${focusedField === 'message' ? 'focused' : ''} ${formData.message ? 'filled' : ''}`}>
                  <label htmlFor="message">Tell us about your project *</label>
                  <div className="input-wrapper">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      required
                      rows="5"
                      placeholder="Share your vision, goals, and any specific requirements..."
                    ></textarea>
                    <div className="input-border"></div>
                  </div>
                </div>

                <button type="submit" className="btn-primary btn-large btn-full magnetic">
                  <span className="btn-text">Send Message</span>
                  <span className="btn-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M18.3333 1.66666L9.16666 10.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.3333 1.66666L12.5 18.3333L9.16666 10.8333L1.66666 7.49999L18.3333 1.66666Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                <div className="trust-indicators">
                  <div className="trust-item">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66666 10 1.66666C5.39763 1.66666 1.66666 5.39763 1.66666 10C1.66666 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7.5 10L9.16666 11.6667L12.5 8.33334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>SSL Secured</span>
                  </div>
                  <div className="trust-item">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1.66666L12.575 7.06666L18.3333 8.19166L14.1667 12.3083L15.15 18.3333L10 15.6083L4.85 18.3333L5.83333 12.3083L1.66666 8.19166L7.425 7.06666L10 1.66666Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Shopify Partner</span>
                  </div>
                  <div className="trust-item">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1.66666V18.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M1.66666 10H18.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="10" cy="10" r="6.66666" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>GDPR Compliant</span>
                  </div>
                  <div className="trust-item">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1.66666L12.5 7.5L18.75 8.33334L14.375 12.5L15.4167 18.75L10 15.8333L4.58333 18.75L5.625 12.5L1.25 8.33334L7.5 7.5L10 1.66666Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="contact-info-wrapper">
            <div className={`contact-card ${visibleCards.includes(0) ? 'visible' : ''}`}>
              <div className="card-background">
                <div className="card-glow"></div>
              </div>
              <div className="card-icon">
                <div className="icon-particles">
                  <span className="icon-particle"></span>
                  <span className="icon-particle"></span>
                </div>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M26.6667 5.33334H5.33333C3.86058 5.33334 2.66667 6.52725 2.66667 8.00001V24C2.66667 25.4728 3.86058 26.6667 5.33333 26.6667H26.6667C28.1394 26.6667 29.3333 25.4728 29.3333 24V8.00001C29.3333 6.52725 28.1394 5.33334 26.6667 5.33334Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2.66667 8L16 17.3333L29.3333 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Email Us</h3>
              <p>Drop us a line anytime</p>
              <a href="mailto:hello@storezi.app">hello@storezi.app</a>
            </div>

            <div className={`contact-card ${visibleCards.includes(1) ? 'visible' : ''}`}>
              <div className="card-background">
                <div className="card-glow"></div>
              </div>
              <div className="card-icon">
                <div className="icon-particles">
                  <span className="icon-particle"></span>
                  <span className="icon-particle"></span>
                </div>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 28C22.6274 28 28 22.6274 28 16C28 9.37259 22.6274 4 16 4C9.37259 4 4 9.37259 4 16C4 22.6274 9.37259 28 16 28Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 10.6667V16H21.3333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Response Time</h3>
              <p>Quick turnaround guaranteed</p>
              <span className="highlight">Within 24 hours</span>
            </div>

            <div className={`contact-card ${visibleCards.includes(2) ? 'visible' : ''}`}>
              <div className="card-background">
                <div className="card-glow"></div>
              </div>
              <div className="card-icon">
                <div className="icon-particles">
                  <span className="icon-particle"></span>
                  <span className="icon-particle"></span>
                </div>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 4C13.8783 4 11.8434 4.84285 10.3431 6.34315C8.84285 7.84344 8 9.87827 8 12C8 17.3333 16 28 16 28C16 28 24 17.3333 24 12C24 9.87827 23.1571 7.84344 21.6569 6.34315C20.1566 4.84285 18.1217 4 16 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="16" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Location</h3>
              <p>Remote-first, global reach</p>
              <span className="highlight">Worldwide</span>
            </div>

          </div>
        </div>

        <div className={`cta-banner fade-in ${visibleCards.includes(3) ? 'visible' : ''}`}>
          <div className="cta-banner-background">
            <div className="cta-banner-glow"></div>
            <div className="cta-banner-grid"></div>
            <div className="cta-banner-orb cta-banner-orb-1"></div>
            <div className="cta-banner-orb cta-banner-orb-2"></div>
          </div>
          <div className="cta-banner-content">
            <div className="cta-banner-text">
              <h3>Prefer to talk?</h3>
              <p>Schedule a free 30-minute consultation to discuss your project in detail.</p>
            </div>
            <a href="https://calendly.com/comzi-official/30min" target="_blank" rel="noopener noreferrer" className="btn-primary btn-large magnetic">
              <span className="btn-text">Book a Call</span>
              <span className="btn-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4.16666 10H15.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 4.16666L15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
